// src/Components/Admin/SettingsRestricted.js

import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

// Firestore & Storage Imports
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { ref as sRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../Firebase/firebase';

import { MenuUpload } from '../../assets/icons/CustomIconsAdd2';
import RichTextEditor from '../../utils/RichTextEditor';

// --- STYLING CONSTANTS ---
const ORANGE_COLOR = '#FF9900';
const LIGHT_BLUE_BG = '#E3F2FD';
const DARK_BLUE = '#00008B';

// LOGO UPLOAD ICON SETTINGS
const LOGO_UPLOAD_SCALE = 6.9;
const LOGO_UPLOAD_ICON_X = 18;
const LOGO_UPLOAD_ICON_Y = 18;
const LOGO_UPLOAD_TEXT_SIZE = '20px';
const LOGO_UPLOAD_TEXT_Y = 110;

const labelStyle = {
  color: DARK_BLUE,
  fontWeight: 'bold',
  fontSize: '0.65rem',
  textTransform: 'uppercase',
  marginBottom: '0px',
  marginLeft: '2px',
  lineHeight: 1.2,
};

const valueBoxStyle = {
  borderRadius: '8px',
  minHeight: '33px', // Visually matches TextField size='small'
  display: 'flex',
  alignItems: 'center',
  padding: '0 8px',
  overflow: 'hidden',
  transition: 'background-color 0.3s ease',
  border: `1px solid ${ORANGE_COLOR}`,
  backgroundColor: LIGHT_BLUE_BG,
};

const RestrictedSettings = forwardRef(
  ({ onDirtyChange, rteFontType, rteFontSize }, ref) => {
    // --- STATE ---

    // Icon (Row 2 Col 1&2 Merged)
    const [iconUrl, setIconUrl] = useState('');
    const [originalIconUrl, setOriginalIconUrl] = useState('');
    const [iconFile, setIconFile] = useState(null);

    // Hover Text (Row 2 Col 1&2 Merged)
    const [hoverText, setHoverText] = useState('');
    const [originalHoverText, setOriginalHoverText] = useState('');

    // Hover Image (Row 1 Col 2)
    const [hoverImageUrl, setHoverImageUrl] = useState('');
    const [originalHoverImageUrl, setOriginalHoverImageUrl] = useState('');
    const [hoverImageFile, setHoverImageFile] = useState(null);

    // Photo/Clip (Row 1 Col 4)
    const [photoUrl, setPhotoUrl] = useState('');
    const [originalPhotoUrl, setOriginalPhotoUrl] = useState('');
    const [photoFile, setPhotoFile] = useState(null);
    const [isVideo, setIsVideo] = useState(false);
    const [originalIsVideo, setOriginalIsVideo] = useState(false);
    const [isAudio, setIsAudio] = useState(false);
    const [originalIsAudio, setOriginalIsAudio] = useState(false);

    // RTE Description (Row 2 Col 3&4 Merged)
    const [description, setDescription] = useState('');
    const [originalDescription, setOriginalDescription] = useState('');

    const [makeUppercase, setMakeUppercase] = useState(true);

    // --- REFS ---
    const iconInputRef = useRef(null);
    const hoverTextInputRef = useRef(null);
    const hoverImageInputRef = useRef(null);
    const photoInputRef = useRef(null);
    const cursorPositionRef = useRef(null);

    // --- DIRTY CHECKING ---
    const isDirty =
      iconUrl !== originalIconUrl ||
      hoverText !== originalHoverText ||
      hoverImageUrl !== originalHoverImageUrl ||
      photoUrl !== originalPhotoUrl ||
      isVideo !== originalIsVideo ||
      isAudio !== originalIsAudio ||
      description !== originalDescription;

    useEffect(() => {
      if (onDirtyChange) {
        onDirtyChange(isDirty);
      }
    }, [isDirty, onDirtyChange]);

    // --- EFFECTS ---

    // 1. Fetch Formatting Rule
    useEffect(() => {
      const unsubFmt = onSnapshot(doc(db, 'settings', 'TextFormatting'), (snap) => {
        if (snap.exists()) {
          const fmtData = snap.data();
          setMakeUppercase(
            fmtData.makeUppercase !== undefined ? !!fmtData.makeUppercase : true,
          );
        } else {
          setMakeUppercase(true);
        }
      });

      return () => {
        unsubFmt();
      };
    }, []);

    // 2. Restore Cursor Position on Formatting
    useEffect(() => {
      if (hoverTextInputRef.current && cursorPositionRef.current !== null) {
        hoverTextInputRef.current.setSelectionRange(
          cursorPositionRef.current,
          cursorPositionRef.current,
        );
        cursorPositionRef.current = null; // Reset after snapping it back
      }
    }, [hoverText]);

    // 3. Initial Firestore Fetch
    useEffect(() => {
      const fetchRestrictedSettings = async () => {
        try {
          const docRef = doc(db, 'settings', 'RestrictedMarkerSettings');
          const snap = await getDoc(docRef);
          if (snap.exists()) {
            const data = snap.data();

            if (data.RestrictedIcon !== undefined) {
              setIconUrl(data.RestrictedIcon);
              setOriginalIconUrl(data.RestrictedIcon);
            }
            if (data.RestrictedHoverText !== undefined) {
              setHoverText(data.RestrictedHoverText);
              setOriginalHoverText(data.RestrictedHoverText);
            }
            if (data.RestrictedHoverImage !== undefined) {
              setHoverImageUrl(data.RestrictedHoverImage);
              setOriginalHoverImageUrl(data.RestrictedHoverImage);
            }
            if (data.RestrictedImage !== undefined) {
              setPhotoUrl(data.RestrictedImage);
              setOriginalPhotoUrl(data.RestrictedImage);
            }
            if (data.RestrictedImageIsVideo !== undefined) {
              setIsVideo(data.RestrictedImageIsVideo);
              setOriginalIsVideo(data.RestrictedImageIsVideo);
            }
            if (data.RestrictedImageIsAudio !== undefined) {
              setIsAudio(data.RestrictedImageIsAudio);
              setOriginalIsAudio(data.RestrictedImageIsAudio);
            }
            if (data.RestrictedDescription !== undefined) {
              setDescription(data.RestrictedDescription);
              setOriginalDescription(data.RestrictedDescription);
            }
          }
        } catch (error) {
          console.error('Error fetching restricted settings:', error);
        }
      };
      fetchRestrictedSettings();
    }, []);

    // --- EXPOSE METHODS TO PARENT (Settings.js) ---
    useImperativeHandle(ref, () => ({
      save: async () => {
        let finalIconUrl = originalIconUrl;
        let finalHoverImageUrl = originalHoverImageUrl;
        let finalPhotoUrl = originalPhotoUrl;

        // 1. Upload Icon
        if (iconFile) {
          const storagePath = sRef(storage, 'restricted_marker_images/restricted_icon');
          const snapshot = await uploadBytes(storagePath, iconFile);
          finalIconUrl = await getDownloadURL(snapshot.ref);
        } else if (iconUrl === '') {
          finalIconUrl = '';
        }

        // 2. Upload Hover Image
        if (hoverImageFile) {
          const storagePath = sRef(
            storage,
            'restricted_marker_images/restricted_hover_image',
          );
          const snapshot = await uploadBytes(storagePath, hoverImageFile);
          finalHoverImageUrl = await getDownloadURL(snapshot.ref);
        } else if (hoverImageUrl === '') {
          finalHoverImageUrl = '';
        }

        // 3. Upload Photo/Clip
        if (photoFile) {
          const storagePath = sRef(storage, 'restricted_marker_images/restricted_image');
          const snapshot = await uploadBytes(storagePath, photoFile);
          finalPhotoUrl = await getDownloadURL(snapshot.ref);
        } else if (photoUrl === '') {
          finalPhotoUrl = '';
        }

        // Merge true prevents obliterating other fields
        const docRef = doc(db, 'settings', 'RestrictedMarkerSettings');
        await setDoc(
          docRef,
          {
            RestrictedIcon: finalIconUrl,
            RestrictedHoverText: hoverText,
            RestrictedHoverImage: finalHoverImageUrl,
            RestrictedImage: finalPhotoUrl,
            RestrictedImageIsVideo: isVideo,
            RestrictedImageIsAudio: isAudio,
            RestrictedDescription: description,
          },
          { merge: true },
        );

        // Update Local Origins
        setOriginalIconUrl(finalIconUrl);
        setIconUrl(finalIconUrl);
        setIconFile(null);

        setOriginalHoverText(hoverText);

        setOriginalHoverImageUrl(finalHoverImageUrl);
        setHoverImageUrl(finalHoverImageUrl);
        setHoverImageFile(null);

        setOriginalPhotoUrl(finalPhotoUrl);
        setPhotoUrl(finalPhotoUrl);
        setPhotoFile(null);
        setOriginalIsVideo(isVideo);
        setOriginalIsAudio(isAudio);

        setOriginalDescription(description);
      },
      revert: () => {
        setIconUrl(originalIconUrl);
        setIconFile(null);

        setHoverText(originalHoverText);

        setHoverImageUrl(originalHoverImageUrl);
        setHoverImageFile(null);

        setPhotoUrl(originalPhotoUrl);
        setPhotoFile(null);
        setIsVideo(originalIsVideo);
        setIsAudio(originalIsAudio);

        setDescription(originalDescription);
      },
      isDirty: () => isDirty,
    }));

    // --- HANDLERS ---

    const stopProp = (e) => {
      e.stopPropagation();
      if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
        e.nativeEvent.stopImmediatePropagation();
      }
    };

    // Icon Handlers
    const handleIconClick = () => iconInputRef.current?.click();
    const handleIconChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setIconFile(file);
        setIconUrl(URL.createObjectURL(file));
      }
      if (iconInputRef.current) iconInputRef.current.value = '';
    };

    // Hover Image Handlers
    const handleHoverImageClick = () => hoverImageInputRef.current?.click();
    const handleHoverImageChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setHoverImageFile(file);
        setHoverImageUrl(URL.createObjectURL(file));
      }
      if (hoverImageInputRef.current) hoverImageInputRef.current.value = '';
    };
    const handleHoverImageClear = () => {
      setHoverImageUrl('');
      setHoverImageFile(null);
      if (hoverImageInputRef.current) hoverImageInputRef.current.value = '';
    };

    // Photo/Clip Handlers
    const handlePhotoClick = () => photoInputRef.current?.click();
    const handlePhotoChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setPhotoFile(file);
        setPhotoUrl(URL.createObjectURL(file));
        setIsVideo(file.type.startsWith('video/'));
        setIsAudio(file.type.startsWith('audio/'));
      }
      if (photoInputRef.current) photoInputRef.current.value = '';
    };
    const handlePhotoClear = () => {
      setPhotoUrl('');
      setPhotoFile(null);
      setIsVideo(false);
      setIsAudio(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    };

    return (
      <Box sx={{ mt: 1 }}>
        <Typography sx={{ color: '#000000', fontWeight: 'normal', mb: 0.5 }}>
          Restricted Markers
        </Typography>

        <Paper
          variant='outlined'
          sx={{
            p: 1,
            borderColor: ORANGE_COLOR,
            borderWidth: '1px',
            borderRadius: 1,
            backgroundColor: 'transparent',
            minHeight: '200px',
            display: 'flex',
            flexDirection: 'column',
          }}>
          <TableContainer sx={{ flexGrow: 1 }}>
            <Table
              size='small'
              sx={{
                width: '100%',
                tableLayout: 'fixed',
                '& td, & th': { borderBottom: 0, py: 0.25, px: 1 },
              }}>
              <TableBody>
                <TableRow>
                  {/* ROW 1, COL 1: Upload Icons for Hover Image */}
                  <TableCell
                    sx={{
                      width: '12.5%',
                      verticalAlign: 'middle',
                    }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}>
                      <IconButton
                        sx={{ p: 0, pb: 1, borderRadius: 2 }}
                        onClick={handleHoverImageClick}>
                        <MenuUpload
                          sx={{ fontSize: 65, overflow: 'visible' }}
                          iconScale={LOGO_UPLOAD_SCALE}
                          textSize={LOGO_UPLOAD_TEXT_SIZE}
                          textY={LOGO_UPLOAD_TEXT_Y}
                          iconX={LOGO_UPLOAD_ICON_X}
                          iconY={LOGO_UPLOAD_ICON_Y}
                          label='Upload'
                        />
                      </IconButton>
                      <input
                        type='file'
                        ref={hoverImageInputRef}
                        style={{ display: 'none' }}
                        accept='image/*'
                        onChange={handleHoverImageChange}
                      />
                    </Box>
                  </TableCell>

                  {/* ROW 1, COL 2: Hover Image Placeholder */}
                  <TableCell
                    sx={{
                      width: '37.5%',
                      borderRight: `1px solid ${ORANGE_COLOR}`,
                      verticalAlign: 'middle',
                    }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        alignItems: 'center',
                      }}>
                      <Box sx={{ width: 'auto', textAlign: 'left' }}>
                        <Typography sx={labelStyle}>HOVER IMAGE</Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          padding: '0px',
                          boxShadow: '2px 2px 5px rgba(0,0,0,0.15)',
                          border: `1px solid ${ORANGE_COLOR}`,
                          height: '220px',
                          width: '220px',
                          overflow: 'hidden',
                          position: 'relative',
                        }}>
                        {hoverImageUrl ? (
                          <>
                            <img
                              src={hoverImageUrl}
                              alt='Hover'
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                              }}
                            />
                            <IconButton
                              size='small'
                              onClick={(e) => {
                                e.stopPropagation();
                                handleHoverImageClear();
                              }}
                              sx={{
                                position: 'absolute',
                                top: '4px',
                                right: '4px',
                                width: '24px',
                                height: '24px',
                                padding: '0px',
                                backgroundColor: '#ffffff',
                                '&:hover': { backgroundColor: '#f5f5f5' },
                                zIndex: 20,
                              }}>
                              <Typography
                                sx={{
                                  fontSize: '14px',
                                  color: '#d32f2f',
                                  fontWeight: 'bold',
                                  lineHeight: 1,
                                }}>
                                X
                              </Typography>
                            </IconButton>
                          </>
                        ) : (
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ textAlign: 'center' }}>
                            Image Placeholder
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>

                  {/* ROW 1, COL 3: Upload Icons for Photo/Clip */}
                  <TableCell
                    sx={{
                      width: '12.5%',
                      verticalAlign: 'middle',
                    }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}>
                      <IconButton
                        sx={{ p: 0, pb: 1, borderRadius: 2 }}
                        onClick={handlePhotoClick}>
                        <MenuUpload
                          sx={{ fontSize: 65, overflow: 'visible' }}
                          iconScale={LOGO_UPLOAD_SCALE}
                          textSize={LOGO_UPLOAD_TEXT_SIZE}
                          textY={LOGO_UPLOAD_TEXT_Y}
                          iconX={LOGO_UPLOAD_ICON_X}
                          iconY={LOGO_UPLOAD_ICON_Y}
                          label='Upload'
                        />
                      </IconButton>
                      <input
                        type='file'
                        ref={photoInputRef}
                        style={{ display: 'none' }}
                        accept='image/*,video/*,audio/*'
                        onChange={handlePhotoChange}
                      />
                    </Box>
                  </TableCell>

                  {/* ROW 1, COL 4: Photo/Clip Placeholder */}
                  <TableCell sx={{ width: '37.5%', verticalAlign: 'middle' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        alignItems: 'center',
                      }}>
                      <Box sx={{ width: 'auto', textAlign: 'left' }}>
                        <Typography sx={labelStyle}>PHOTO/CLIP</Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          padding: '0px',
                          boxShadow: '2px 2px 5px rgba(0,0,0,0.15)',
                          border: `1px solid ${ORANGE_COLOR}`,
                          height: '220px',
                          width: '220px',
                          overflow: 'hidden',
                          position: 'relative',
                        }}>
                        {photoUrl ? (
                          <>
                            {isVideo ? (
                              <video
                                src={photoUrl}
                                controls
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  display: 'block',
                                }}
                              />
                            ) : isAudio ? (
                              <audio
                                src={photoUrl}
                                controls
                                style={{
                                  width: '90%',
                                  display: 'block',
                                }}
                              />
                            ) : (
                              <img
                                src={photoUrl}
                                alt='Restricted'
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  display: 'block',
                                }}
                              />
                            )}
                            <IconButton
                              size='small'
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePhotoClear();
                              }}
                              sx={{
                                position: 'absolute',
                                top: '4px',
                                right: '4px',
                                width: '24px',
                                height: '24px',
                                padding: '0px',
                                backgroundColor: '#ffffff',
                                '&:hover': { backgroundColor: '#f5f5f5' },
                                zIndex: 20,
                              }}>
                              <Typography
                                sx={{
                                  fontSize: '14px',
                                  color: '#d32f2f',
                                  fontWeight: 'bold',
                                  lineHeight: 1,
                                }}>
                                X
                              </Typography>
                            </IconButton>
                          </>
                        ) : (
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ textAlign: 'center' }}>
                            Image/Video/Audio Placeholder
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>

                <TableRow>
                  {/* ROW 2, MERGED COL 1 & 2: White Rectangle Housing Inputs */}
                  <TableCell
                    colSpan={2}
                    sx={{
                      width: '50%',
                      borderRight: `1px solid ${ORANGE_COLOR}`,
                      verticalAlign: 'top',
                      height: '100%',
                    }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: '#ffffff',
                        borderRadius: 0,
                        border: '1px solid #000000',
                        width: '100%',
                        height: '180px',
                        overflow: 'hidden',
                        p: 1.5,
                        gap: 0,
                      }}>
                      <Table
                        sx={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          tableLayout: 'fixed',
                        }}>
                        <TableBody>
                          {/* 1. HOVER TEXT ROW */}
                          <TableRow>
                            <TableCell sx={{ borderBottom: 'none', padding: 0 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  width: '100%',
                                }}>
                                <Typography sx={labelStyle}>HOVER TEXT</Typography>
                                <Box
                                  onMouseDown={stopProp}
                                  style={{ pointerEvents: 'auto' }}
                                  sx={{
                                    ...valueBoxStyle,
                                    position: 'relative',
                                    zIndex: 10,
                                  }}>
                                  <input
                                    type='text'
                                    ref={hoverTextInputRef}
                                    value={hoverText || ''}
                                    onClick={stopProp}
                                    onChange={(e) => {
                                      const currentCursorPos = e.target.selectionStart;
                                      let finalValue = e.target.value;
                                      const nativeEvent = e.nativeEvent;

                                      if (makeUppercase) {
                                        if (
                                          nativeEvent &&
                                          nativeEvent.inputType === 'insertFromPaste'
                                        ) {
                                          const selectionStart = e.target.selectionStart;
                                          if (
                                            nativeEvent.data &&
                                            typeof selectionStart === 'number'
                                          ) {
                                            const dataLen = nativeEvent.data.length;
                                            const start = selectionStart - dataLen;
                                            if (
                                              start >= 0 &&
                                              finalValue.substring(
                                                start,
                                                selectionStart,
                                              ) === nativeEvent.data
                                            ) {
                                              finalValue =
                                                finalValue.slice(0, start) +
                                                nativeEvent.data.toUpperCase() +
                                                finalValue.slice(selectionStart);
                                            } else {
                                              finalValue = finalValue.toUpperCase();
                                            }
                                          } else {
                                            finalValue = finalValue.toUpperCase();
                                          }
                                        } else if (
                                          nativeEvent &&
                                          nativeEvent.data &&
                                          typeof currentCursorPos === 'number' &&
                                          nativeEvent.inputType === 'insertText'
                                        ) {
                                          const dataLen = nativeEvent.data.length;
                                          const start = currentCursorPos - dataLen;

                                          if (
                                            start >= 0 &&
                                            finalValue.substring(
                                              start,
                                              currentCursorPos,
                                            ) === nativeEvent.data
                                          ) {
                                            const inverted = nativeEvent.data
                                              .split('')
                                              .map((c) =>
                                                c === c.toUpperCase()
                                                  ? c.toLowerCase()
                                                  : c.toUpperCase(),
                                              )
                                              .join('');

                                            finalValue =
                                              finalValue.slice(0, start) +
                                              inverted +
                                              finalValue.slice(currentCursorPos);
                                          } else {
                                            finalValue = finalValue.toUpperCase();
                                          }
                                        } else if (
                                          nativeEvent &&
                                          nativeEvent.inputType &&
                                          nativeEvent.inputType.startsWith('delete')
                                        ) {
                                          // Let deletion happen naturally
                                        } else {
                                          finalValue = finalValue.toUpperCase();
                                        }
                                      }

                                      cursorPositionRef.current = currentCursorPos;
                                      setHoverText(finalValue);
                                    }}
                                    style={{
                                      border: 'none',
                                      outline: 'none',
                                      width: '100%',
                                      backgroundColor: 'transparent',
                                      fontSize: '0.8rem',
                                      lineHeight: '1.2',
                                      color: DARK_BLUE,
                                      padding: '6px 0',
                                      margin: 0,
                                    }}
                                  />
                                </Box>
                              </Box>
                            </TableCell>
                          </TableRow>

                          {/* SPACER ROW FOR INCREASED SPACING */}
                          <TableRow>
                            <TableCell
                              sx={{ borderBottom: 'none', padding: 0, height: '8px' }}
                            />
                          </TableRow>

                          {/* 2. UPLOAD ICON ROW */}
                          <TableRow>
                            <TableCell
                              sx={{
                                borderTop: '1px solid #000000',
                                borderBottom: '1px solid #000000 !important',
                                padding: 0,
                                paddingTop: 2.5,
                                paddingBottom: 2.5,
                              }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  width: '100%',
                                  gap: 2,
                                }}>
                                <Box
                                  sx={{
                                    ...valueBoxStyle,
                                    flexGrow: 1,
                                    backgroundColor: '#ffffff',
                                  }}>
                                  <Typography
                                    sx={{
                                      fontSize: '0.8rem',
                                      color: DARK_BLUE,
                                      userSelect: 'none',
                                      pointerEvents: 'none',
                                    }}>
                                    Upload Restricted Marker Icon
                                  </Typography>
                                </Box>

                                <Tooltip title='Click to Upload Restricted Marker'>
                                  <Box
                                    sx={{
                                      display: 'inline-flex',
                                      cursor: 'pointer',
                                      flexShrink: 0,
                                    }}
                                    onClick={handleIconClick}>
                                    {iconUrl ? (
                                      <Box
                                        component='img'
                                        src={iconUrl}
                                        alt='Restricted Marker'
                                        sx={{
                                          width: 28,
                                          height: 28,
                                          objectFit: 'contain',
                                        }}
                                      />
                                    ) : (
                                      <MenuUpload
                                        sx={{ fontSize: 28 }}
                                        iconScale={2}
                                        textSize={'8px'}
                                        textY={70}
                                        iconX={18}
                                        iconY={18}
                                        label='Upload'
                                      />
                                    )}
                                  </Box>
                                </Tooltip>

                                <input
                                  type='file'
                                  ref={iconInputRef}
                                  style={{ display: 'none' }}
                                  accept='image/*'
                                  onChange={handleIconChange}
                                />
                              </Box>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Box>
                  </TableCell>

                  {/* ROW 2, MERGED COL 3 & 4: Rich Text Editor */}
                  <TableCell
                    colSpan={2}
                    sx={{ width: '50%', verticalAlign: 'top', height: '100%' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: '#fff',
                        borderRadius: 0,
                        border: '1px solid #000000',
                        width: '100%',
                        height: '180px',
                        overflow: 'hidden',
                      }}>
                      {/* AMENDED: Passed defaults to the RTE component */}
                      <RichTextEditor
                        initialContent={description}
                        onUpdate={(val) => setDescription(val)}
                        readOnly={false}
                        defaultFontType={rteFontType}
                        defaultFontSize={rteFontSize}
                      />
                    </Box>
                  </TableCell>
                </TableRow>

                {/* Invisible Spacer Row to terminate the orange vertical borders cleanly */}
                <TableRow>
                  <TableCell colSpan={4} sx={{ height: '12px', padding: 0, border: 0 }} />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  },
);

export default RestrictedSettings;
